import { Role, UserType } from '@texas/api/endpoints/userApi';
import { convertToEnum, ensureEnumNumber } from './enumHelpers';
import { TFunction } from 'i18next';

export function hasRole(role: Role | null, requiredRoles: Role[]) {
  if (role === null) return false;
  return requiredRoles.includes(convertToEnum(Role, role));
}

export function roleFriendlyName(role: Role, t: TFunction) {
  switch (role) {
    case Role.SystemAdministrator:
      return t('auth.roles.systemAdmin.title');
    case Role.InternalAdministrator:
      return t('auth.roles.admin.title');
    case Role.InternalAgent:
      return t('auth.roles.agent.title');
    case Role.Brand:
      return t('auth.roles.brand.title');
    case Role.Supplier:
      return t('auth.roles.supplier.title');
  }
}

export function userTypeFriendlyName(value: UserType, t: TFunction) {
  switch (value) {
    case UserType.BrandCoordinator:
      return t('user.userType.brandCoordinator');
    case UserType.ProductDeveloper:
      return t('user.userType.productDeveloper');
    case UserType.KeyAccountManager:
      return t('user.userType.keyAccountManager');
    case UserType.CustomerService:
      return t('user.userType.customerService');
    case UserType.Supplier:
      return t('user.userType.supplierContact');
    case UserType.Brand:
      return t('user.userType.brandContact');
  }
}

export function userTypeShortName(value: UserType, t: TFunction) {
  switch (value) {
    case UserType.BrandCoordinator:
      return t('user.userType.short.brandCoordinator');
    case UserType.ProductDeveloper:
      return t('user.userType.short.productDeveloper');
    case UserType.KeyAccountManager:
      return t('user.userType.short.keyAccountManager');
    case UserType.CustomerService:
      return t('user.userType.short.customerService');
    case UserType.Supplier:
    case UserType.Brand:
      return '';
  }
}

export function customerUserRoleLabel(
  currentUserRoles: (string | UserType)[],
  currentUserCustomRoles: string[],
  t: TFunction,
): string | null {
  const labels = [
    ...currentUserRoles.map((role) =>
      userTypeShortName(ensureEnumNumber(UserType, role as UserType), t),
    ),
    ...currentUserCustomRoles,
  ].filter((label): label is string => Boolean(label));

  return labels.length > 0 ? [...new Set(labels)].join(' · ') : null;
}

export function roleDescription(role: Role, t: TFunction) {
  switch (role) {
    case Role.SystemAdministrator:
      return t('auth.roles.systemAdmin.desc');
    case Role.InternalAdministrator:
      return t('auth.roles.admin.desc');
    case Role.InternalAgent:
      return t('auth.roles.agent.desc');
    case Role.Brand:
      return t('auth.roles.brand.desc');
    case Role.Supplier:
      return t('auth.roles.supplier.desc');
  }
}

export function internalRole(role: Role) {
  return (
    role === Role.InternalAdministrator ||
    role === Role.InternalAgent ||
    role === Role.SystemAdministrator
  );
}
